<template>
  <navigation-drawer>
    <template v-if="!$vuetify.display.mdAndDown" #navigation>
      <master-layout-navigation v-model="value" :navigation="navigation" @click-navigation="onNavigationClick" />
    </template>
    <template #main>
      <div class="master">
        <div class="master__header">
          <slot name="header" />
        </div>
        <div class="master__content-wrapper">
          <div v-if="slots.subheader" class="master__subheader">
            <slot name="subheader" />
          </div>
          <div class="master__content">
            <master-layout-navigation
              v-if="$vuetify.display.mdAndDown"
              v-model="value"
              :navigation="navigation"
              @click-navigation="onNavigationClick"
            />
            <slot />
          </div>
        </div>
      </div>
    </template>
  </navigation-drawer>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import type { NavigationItem } from '@/core/types/components/components';
import NavigationDrawer from '@/core/layouts/components/NavigationDrawer.vue';
import MasterLayoutNavigation from '@/core/layouts/components/master/MasterLayoutNavigation.vue';

const props = defineProps<{
  modelValue: number;
  navigation: NavigationItem[];
}>();

const slots = defineSlots<{
  header: () => void;
  subheader?: () => void;
  default: () => void;
}>();

const value = useVModel(props, 'modelValue');

const emit = defineEmits<{
  (event: 'update:modelValue', index: number): void;
  (event: 'clickNavigation', index: number, item: NavigationItem): void;
}>();

const onNavigationClick = async (index: number) => {
  const item = props.navigation[index];

  if (item) {
    return;
  }

  emit('clickNavigation', index, item);
};
</script>

<style lang="scss" scoped>
.master {
  display: flex;
  flex-direction: column;

  height: 100%;

  @include lg-and-down {
    padding-right: 24px;
  }

  @include md-and-down {
    padding: 0;
  }

  &__content {
    display: flex;
    flex-grow: 1;
    flex-direction: column;

    width: 100%;
    max-width: 640px;
    margin: 48px auto 0;

    @include md-and-down {
      margin-top: 24px;
    }

    @include sm-and-down {
      height: 100%;
      margin-top: 0;
      margin-bottom: 100px;
    }

    :deep([data-wide-content]) {
      align-self: center;

      width: 860px;
      max-width: 860px;

      @include md-and-down {
        max-width: calc(100vw - 48px);
      }

      @include sm-and-down {
        max-width: 100%;
      }
    }

    &-wrapper {
      display: flex;
      flex-direction: column;

      height: 100%;
      margin-top: 16px;
      padding: 24px;

      background-color: $main-bg-color;
      border-radius: $border-radius-xl;

      @include md-and-down {
        margin-top: 12px;

        border-radius: $border-radius-xl $border-radius-xl 0 0;
      }

      @include sm-and-down {
        margin-top: 0;
      }
    }
  }

  &__header {
    @include md-and-down {
      padding: 8px 16px;
    }

    @include sm-and-down {
      padding: 8px 16px;
    }
  }

  &__subheader {
    @include sm-and-down {
      margin-bottom: 24px;
    }
  }
}
</style>
